import { network } from "../config";
import { DialogChat } from "../types/DialogChat";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import {
  ParamsAvailable,
  SpecialistData,
  TypeSpecialistFilter,
} from "../types/TypeSpecialist";
import {
  GetUserAvatar,
  TypeUpdatedUserInfo,
  TypeUserData,
} from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import {
  SignedReadUrlResponse,
  UploadUrlRequest,
  UploadUrlResponse,
  resolveSignedReadUrl,
} from "../utils/storedFileReferences";
import customKy from "./kyExtend";
const { users } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "users/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class UsersApi {
  static async getAvatarUploadUrl(
    token: string,
    userId: string,
    payload: UploadUrlRequest,
  ): Promise<UploadUrlResponse> {
    return await customKy
      .post(url(baseURL, users.getAvatarUploadUrl, { id: userId }), {
        headers: getAuthHeader(token),
        json: payload,
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async updateAvatarS3(
    token: string,
    userId: string,
    avatarS3: string,
  ): Promise<TypeDefaultResponse & { user?: TypeUserData }> {
    return await customKy
      .patch(url(baseURL, users.updateAvatarS3, { id: userId }), {
        headers: getAuthHeader(token),
        json: { avatarS3 },
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async getAvatarS3SignedUrl(
    userId: string,
    filepath: string,
    token?: string,
  ): Promise<SignedReadUrlResponse> {
    return await customKy
      .post(url(baseURL, users.getAvatarS3SignedUrl, { id: userId }), {
        headers: token ? getAuthHeader(token) : undefined,
        json: { filepath },
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  private static async resolveAvatarResponse(
    response: GetUserAvatar,
    token?: string,
  ): Promise<GetUserAvatar> {
    const signedAvatar = await resolveSignedReadUrl({
      storedFileReference: response.avatarS3,
      getSignedUrl: (filepath) =>
        UsersApi.getAvatarS3SignedUrl(response._id, filepath, token),
    });

    return {
      ...response,
      avatar: signedAvatar ?? response.avatar,
    };
  }

  static async getUserSelectedFields(
    token: string,
    userId: string,
    userFields: string,
    specFields: string,
  ): Promise<TypeDefaultResponse & { userData?: Partial<TypeUserData> }> {
    return await customKy
      .get(
        url(baseURL, users.getUserSelectedFields, {
          id: userId,
          userFields,
          specFields,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getUserProfileInfoById(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      user?: (TypeUserData & { specialistId?: SpecialistData }) | null;
    }
  > {
    return await customKy
      .get(url(baseURL, users.getUserProfileInfoById, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getUserByToken(token: string): Promise<TypeUserData | null> {
    return await customKy
      .get(url(baseURL, users.getUserByToken, { token }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async auth(
    phoneNumberOrEmail: string,
    password: string,
    selectedLanguage: string,
    rememberMe?: boolean,
  ): Promise<
    TypeDefaultResponse & {
      userData?: TypeUserData | null;
      token?: string;
      refreshToken?: string;
    }
  > {
    return await customKy
      .post(url(baseURL, users.auth), {
        json: {
          phoneNumberOrEmail: phoneNumberOrEmail,
          password: password,
          selectedLanguage: selectedLanguage,
          rememberMe: rememberMe,
        },
      })
      .then((res) => res.json());
  }

  static async getUserAvatarAndName(
    token: string,
    id: string,
  ): Promise<GetUserAvatar> {
    const response: GetUserAvatar = await customKy
      .get(url(baseURL, users.getUserAvatarAndName, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());

    return await UsersApi.resolveAvatarResponse(response, token);
  }

  static async getUserPublicAvatarAndName(id: string): Promise<GetUserAvatar> {
    const response: GetUserAvatar = await customKy
      .get(url(baseURL, users.getUserPublicAvatarAndName, { id }), {})
      .then((res) => res.json());

    return await UsersApi.resolveAvatarResponse(response);
  }

  static async getUserPublicInfoById(
    id: string,
    language: string,
    token?: string,
  ): Promise<
    TypeDefaultResponse & {
      user?: TypeUserData & { specialistIds?: SpecialistData[] };
    }
  > {
    return await customKy
      .get(url(baseURL, users.getUserPublicInfoById, { id, language }), {
        headers: token ? getAuthHeader(token) : undefined,
      })
      .then((res) => res.json());
  }

  static async refreshAccessToken(payload: { refreshToken: string }): Promise<
    TypeDefaultResponse & {
      token?: string;
    }
  > {
    return await customKy
      .patch(url(baseURL, users.refreshAccessToken), {
        json: payload,
      })
      .then((res) => res.json());
  }

  static async logout(payload: {
    userId: string;
  }): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, users.logout), {
        json: payload,
      })
      .then((res) => res.json());
  }

  static async findUserBySearchString(
    token: string,
    searchString: string,
    searchForSpecialistOnly?: "true" | "false",
    servicesConfigFilter?: Record<
      string,
      { switch: boolean; allowed: boolean }
    >,
  ): Promise<TypeDefaultResponse & { users?: TypeUserData[] }> {
    const params = new URLSearchParams();
    if (servicesConfigFilter) {
      params.append(
        "servicesConfigFilter",
        JSON.stringify(servicesConfigFilter),
      );
    }
    return await customKy
      .get(
        url(
          baseURL,
          users.findUserBySearchString,
          {
            string: searchString,
            specialistOnly: searchForSpecialistOnly,
          },
          params,
        ),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getFilteredClients(
    token: string,
    payload: {
      userId: string;
      filters: TypeSpecialistFilter;
      alreadyFetched?: string[];
    },
  ): Promise<
    TypeDefaultResponse & {
      paramsAvailable: ParamsAvailable;
      users?: (TypeUserData & {
        balanceStatistic?: {
          balance: number;
          income: number;
          reserved: number;
          expenses: number;
          withdrawalInProgress: number;
          withdrawn: number;
        };
      })[];
    }
  > {
    return await customKy
      .post(url(baseURL, users.getFilteredClients), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getUsersDialogChats(
    token: string,
    userId: string,
    limit: number,
    page: number,
    name?: string,
  ): Promise<
    TypeDefaultResponse & {
      chats: DialogChat[];
    }
  > {
    const params = new URLSearchParams();
    if (name) {
      params.append("name", name);
    }
    return await customKy
      .get(
        url(
          baseURL,
          users.getUsersDialogChats,
          { id: userId, page, limit },
          params,
        ),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async updateUserData(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<
    TypeDefaultResponse & {
      userData?: TypeUserData | null;
    }
  > {
    return await customKy
      .patch(url(baseURL, users.update, { id: updateData._id }), {
        headers: getAuthHeader(token),
        json: { updateData },
      })
      .then((res) => res.json());
  }
}
